import React, { useState, useEffect } from 'react';
import { ruleAPI } from '../../../utils/api';
import { useNotification } from '../../../context/NotificationContext';

const AdminRules = () => {
    const { showSuccess, showError } = useNotification();
    const [rules, setRules] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [newRule, setNewRule] = useState({
        title: '',
        content: '',
        type: 'terms_and_conditions',
        user_role: 'all',
        version: '1.0'
    });

    useEffect(() => {
        fetchRules();
    }, []);

    const fetchRules = async () => {
        try {
            const res = await ruleAPI.getActive(); // For admin, we might want a different endpoints to see all, but let's start here
            if (res.data?.success) {
                setRules(res.data.data);
            }
        } catch (error) {
            // showError('Failed to fetch rules');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const res = await ruleAPI.create(newRule);
            if (res.data?.success) {
                showSuccess('Rule created successfully');
                setShowModal(false);
                setNewRule({
                    title: '',
                    content: '',
                    type: 'terms_and_conditions',
                    user_role: 'all',
                    version: '1.0'
                });
                fetchRules();
            } else {
                showError(res.data?.message || 'Failed to create rule');
            }
        } catch (error) {
            showError(error.response?.data?.message || 'Error creating rule');
        }
    };

    if (loading) return <div className="text-center py-8">Loading rules...</div>;

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h1 className="text-2xl font-bold text-gold">Rules & Compliance</h1>
                <button
                    onClick={() => setShowModal(true)}
                    className="bg-gold text-dark px-4 py-2 rounded-lg font-bold hover:opacity-90"
                >
                    Create New Rule
                </button>
            </div>

            {rules.length === 0 ? (
                <div className="text-center py-12 bg-gray-800 rounded-xl">
                    <p className="text-gray-400">No active rules found.</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {rules.map((rule) => (
                        <div key={rule._id} className="bg-gray-800 rounded-xl p-6">
                            <div className="flex justify-between items-start mb-4">
                                <h3 className="text-xl font-semibold text-white">{rule.title}</h3>
                                <span className="bg-blue-900/50 text-blue-300 text-xs px-2 py-1 rounded-full">
                                    v{rule.version}
                                </span>
                            </div>
                            <div className="text-sm text-gray-400 mb-4 h-24 overflow-y-auto whitespace-pre-wrap">
                                {rule.content}
                            </div>
                            <div className="flex justify-between items-center text-xs text-gray-500 border-t border-gray-700 pt-4">
                                <span className="capitalize">Type: {rule.type.replace(/_/g, ' ')}</span>
                                <span className="capitalize">Role: {rule.user_role}</span>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Create Rule Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
                    <div className="bg-gray-800 p-6 rounded-xl border border-gold/30 max-w-2xl w-full">
                        <h2 className="text-xl font-bold text-gold mb-4">Create New Rule</h2>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-gray-400 text-sm mb-1">Title</label>
                                    <input
                                        className="w-full bg-gray-700 border border-gray-600 p-3 rounded-lg text-light"
                                        value={newRule.title}
                                        onChange={e => setNewRule({ ...newRule, title: e.target.value })}
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-gray-400 text-sm mb-1">Version</label>
                                    <input
                                        className="w-full bg-gray-700 border border-gray-600 p-3 rounded-lg text-light"
                                        value={newRule.version}
                                        onChange={e => setNewRule({ ...newRule, version: e.target.value })}
                                        required
                                        placeholder="e.g. 1.0"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-gray-400 text-sm mb-1">Type</label>
                                    <select
                                        className="w-full bg-gray-700 border border-gray-600 p-3 rounded-lg text-light"
                                        value={newRule.type}
                                        onChange={e => setNewRule({ ...newRule, type: e.target.value })}
                                    >
                                        <option value="terms_and_conditions">Terms & Conditions</option>
                                        <option value="privacy_policy">Privacy Policy</option>
                                        <option value="doctor_guidelines">Doctor Guidelines</option>
                                        <option value="consultation_disclaimer">Consultation Disclaimer</option>
                                        <option value="patient_consent">Patient Consent</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-gray-400 text-sm mb-1">Target Role</label>
                                    <select
                                        className="w-full bg-gray-700 border border-gray-600 p-3 rounded-lg text-light"
                                        value={newRule.user_role}
                                        onChange={e => setNewRule({ ...newRule, user_role: e.target.value })}
                                    >
                                        <option value="all">All Users</option>
                                        <option value="patient">Patients</option>
                                        <option value="doctor">Doctors</option>
                                        <option value="hospital">Hospitals</option>
                                        <option value="clinic">Clinics</option>
                                        <option value="retailer">Retailers</option>
                                    </select>
                                </div>
                            </div>

                            <div>
                                <label className="block text-gray-400 text-sm mb-1">Content</label>
                                <textarea
                                    className="w-full bg-gray-700 border border-gray-600 p-3 rounded-lg text-light h-40"
                                    value={newRule.content}
                                    onChange={e => setNewRule({ ...newRule, content: e.target.value })}
                                    required
                                />
                            </div>

                            <div className="flex space-x-3 pt-4">
                                <button
                                    type="button"
                                    onClick={() => setShowModal(false)}
                                    className="flex-1 bg-gray-700 text-light py-3 rounded-lg font-bold"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="flex-1 bg-gold text-dark py-3 rounded-lg font-bold"
                                >
                                    Publish Rule
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminRules;
